import React, { ReactElement, useMemo } from 'react'
import ReactWordcloud from 'react-wordcloud'

export interface WordCloudItem {
  text: string
  value: number
}

interface WordCloudChartProps {
  responseTexts: string[]
  height?: number
}

export const WordCloudChart: React.FC<WordCloudChartProps> = ({
  responseTexts,
  height = 300,
}): ReactElement => {
  const words = useMemo(() => {
    if (!responseTexts || responseTexts.length === 0) {
      return []
    }

    // Extract words from response texts (basic tokenization)
    const rawWords: string[] = []

    responseTexts.forEach((responseText) => {
      if (!responseText) return

      // Split by whitespace and basic punctuation, extract words with 3+ letters
      const text = responseText.toLowerCase()
      const wordMatches = text.match(/\b[a-z]{3,}\b/gi) // Words with 3+ letters

      if (wordMatches) {
        wordMatches.forEach((word) => {
          rawWords.push(word.toLowerCase().trim())
        })
      }
    })

    // Filter out common stop words
    const stopWords = new Set([
      'the',
      'be',
      'to',
      'of',
      'and',
      'a',
      'in',
      'that',
      'have',
      'i',
      'it',
      'for',
      'not',
      'on',
      'with',
      'he',
      'as',
      'you',
      'do',
      'at',
      'this',
      'but',
      'his',
      'by',
      'from',
      'they',
      'we',
      'say',
      'her',
      'she',
      'or',
      'an',
      'will',
      'my',
      'one',
      'all',
      'would',
      'there',
      'their',
      'what',
      'so',
      'up',
      'out',
      'if',
      'about',
      'who',
      'get',
      'which',
      'go',
      'me',
      'when',
      'make',
      'can',
      'like',
      'time',
      'no',
      'just',
      'him',
      'know',
      'take',
      'people',
      'into',
      'year',
      'your',
      'good',
      'some',
      'could',
      'them',
      'see',
      'other',
      'than',
      'then',
      'now',
      'look',
      'only',
      'come',
      'its',
      'over',
      'think',
      'also',
      'back',
      'after',
      'use',
      'two',
      'how',
      'our',
      'work',
      'first',
      'well',
      'way',
      'even',
      'new',
      'want',
      'because',
      'any',
      'these',
      'give',
      'day',
      'most',
      'us',
      'is',
      'are',
      'was',
      'were',
      'been',
      'being',
      'has',
      'had',
      'does',
      'did',
      'doing',
      'should',
      'would',
      'could',
      'may',
      'might',
      'must',
      'shall',
      'will',
      'can',
      'ought',
    ])

    // Count word frequencies, filtering stop words
    const wordMap = new Map<string, number>()

    rawWords.forEach((word) => {
      const cleanWord = word.toLowerCase().trim()
      if (cleanWord.length >= 3 && !stopWords.has(cleanWord)) {
        wordMap.set(cleanWord, (wordMap.get(cleanWord) || 0) + 1)
      }
    })

    // Convert to array and sort by frequency
    const wordArray: WordCloudItem[] = Array.from(wordMap.entries())
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 100) // Limit to top 100 words

    return wordArray
  }, [responseTexts])

  const options = useMemo(() => {
    if (words.length === 0) {
      return {
        rotations: 0,
        rotationAngles: [0, 0] as [number, number],
        fontSizes: [12, 12] as [number, number],
        scale: 'sqrt' as const,
        enableOptimizations: true,
        deterministic: true,
      }
    }

    const maxValue = words.reduce((max, w) => (w.value > max ? w.value : max), 0)

    // Derive font sizes relative to the max value, with reasonable clamps
    const derivedMax = Math.max(48, Math.min(72, 16 + Math.floor(Math.sqrt(maxValue) * 8)))
    const derivedMin = Math.min(12, Math.max(12, derivedMax - 14))

    return {
      rotations: 10, // -30, 0, 30
      rotationAngles: [-30, 30] as [number, number],
      fontSizes: [derivedMin, derivedMax] as [number, number],
      scale: 'sqrt' as const,
      enableOptimizations: true,
      deterministic: true,
    }
  }, [words])

  if (!words || words.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        No words to display.
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height }}>
      <ReactWordcloud words={words} options={options} />
    </div>
  )
}
